
import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle, Shield, FileCheck, Clock, RefreshCw, ClipboardList, Cloud, Calendar, Users } from "lucide-react";
import HubSpotWaitlistModal from "@/components/landing/HubSpotWaitlistModal";
import standardlyLogo from "@/assets/standardly-logo.png";

const Index = () => {
  const [showWaitlistForm, setShowWaitlistForm] = useState(false);
  
  const openWaitlistForm = () => {
    setShowWaitlistForm(true);
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navigation Bar */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[rgb(85,64,175)]">
        <div className="container flex h-16 items-center justify-between">
          <div className="flex items-center gap-2">
            <img 
              src={standardlyLogo} 
              alt="Standardly Logo" 
              className="h-8 w-auto" 
            />
          </div>
          <div className="flex items-center gap-4">
            <Button 
              onClick={openWaitlistForm} 
              className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-6"
            >
              Join Waitlist
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-brand-gradient overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/20" />
        
        <div className="container px-4 md:px-6 relative z-10 pt-16">
          <div className="flex flex-col items-center text-center space-y-6 max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white/90 text-sm font-medium backdrop-blur-sm border border-white/20">
              <Cloud className="h-4 w-4" />
              Built for AWS MSP Partners
            </div>
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl lg:text-7xl text-white">
              Simplify Your{" "}
              <br className="hidden sm:block" />
              AWS MSP Audits.{" "}
              <br />
              <span className="text-accent italic">For Good.</span>
            </h1>
            <p className="max-w-[700px] text-white/80 md:text-xl leading-relaxed">
              The only platform purpose-built for AWS Managed Service Providers to track controls, organize evidence, and stay audit-ready across every renewal cycle.
            </p>
            <div className="pt-4">
              <Button 
                size="lg" 
                onClick={openWaitlistForm}
                className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-8 py-6 text-lg font-medium"
              >
                Join Waitlist
              </Button>
            </div>
          </div>
        </div>
      </section>      

      {/* Audit Cycle Section */}
      <section className="py-24 bg-muted/50">
        <div className="container px-4 md:px-6">
          <div className="flex flex-col items-center text-center space-y-4 mb-16">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl text-foreground">
              Master the 3-Year Audit Cycle
            </h2>
            <p className="max-w-[700px] text-muted-foreground text-lg">
              From Full Audits to Performance-Based Renewals, Standardly keeps you organized and prepared.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="flex flex-col items-center text-center space-y-4 p-8 bg-card rounded-2xl shadow-sm border border-border">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-2xl font-bold text-primary">
                Y0
              </div>
              <h3 className="text-xl font-semibold text-card-foreground">Full Audit</h3>
              <p className="text-muted-foreground text-sm">
                Initial comprehensive audit against all prerequisite and technical controls
              </p>
            </div>
            <div className="flex flex-col items-center text-center space-y-4 p-8 bg-card rounded-2xl shadow-sm border border-border">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-2xl font-bold text-primary">
                Y1
              </div>
              <h3 className="text-xl font-semibold text-card-foreground">PBR Renewal</h3>
              <p className="text-muted-foreground text-sm">
                Performance-Based Renewal 365 days from original award date
              </p>
            </div>
            <div className="flex flex-col items-center text-center space-y-4 p-8 bg-card rounded-2xl shadow-sm border border-border">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-2xl font-bold text-primary">
                Y2
              </div>
              <h3 className="text-xl font-semibold text-card-foreground">PBR Renewal</h3>
              <p className="text-muted-foreground text-sm">
                Annual performance review and continuous compliance validation
              </p>
            </div>
            <div className="flex flex-col items-center text-center space-y-4 p-8 bg-card rounded-2xl shadow-sm border border-border">
              <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center text-2xl font-bold text-accent">
                Y3
              </div>
              <h3 className="text-xl font-semibold text-card-foreground">Full Audit</h3>
              <p className="text-muted-foreground text-sm">
                Complete re-validation of all MSP program requirements
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-background">
        <div className="container px-4 md:px-6">
          <div className="flex flex-col items-center text-center space-y-4 mb-16">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl text-foreground">
              Everything You Need for MSP Compliance
            </h2>
            <p className="max-w-[700px] text-muted-foreground text-lg">
              Track every control category—Business, People, Governance, Platform, Security, and Operations.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="flex flex-col items-center text-center space-y-4 p-8 bg-card rounded-2xl shadow-sm border border-border hover:shadow-lg transition-all">
              <div className="p-4 rounded-2xl bg-primary/10">
                <ClipboardList className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-card-foreground">Control Tracking</h3>
              <p className="text-muted-foreground">
                Monitor all prerequisite and technical validation controls in one central dashboard.
              </p>
            </div>
            
            <div className="flex flex-col items-center text-center space-y-4 p-8 bg-card rounded-2xl shadow-sm border border-border hover:shadow-lg transition-all">
              <div className="p-4 rounded-2xl bg-primary/10">
                <FileCheck className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-card-foreground">Evidence Management</h3>
              <p className="text-muted-foreground">
                Organize SOPs, policies, case studies, and certifications with clear guidance on acceptable formats.
              </p>
            </div>
            
            <div className="flex flex-col items-center text-center space-y-4 p-8 bg-card rounded-2xl shadow-sm border border-border hover:shadow-lg transition-all">
              <div className="p-4 rounded-2xl bg-primary/10">
                <Calendar className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-card-foreground">Deadline Alerts</h3>
              <p className="text-muted-foreground">
                Never miss the 30-day pre-audit submission window with automated reminders and timelines.
              </p>
            </div>

            <div className="flex flex-col items-center text-center space-y-4 p-8 bg-card rounded-2xl shadow-sm border border-border hover:shadow-lg transition-all">
              <div className="p-4 rounded-2xl bg-primary/10">
                <Users className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-card-foreground">Team Collaboration</h3>
              <p className="text-muted-foreground">
                Assign controls to team members and track progress across your organization.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Control Categories Section */}
      <section className="py-24 bg-muted/50">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl text-foreground">
              All MSP Control Categories Covered
            </h2>
            <p className="mt-4 text-muted-foreground max-w-3xl mx-auto text-lg">
              From Business Prerequisites to Security Validations—we help you stay compliant across every requirement.
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-card rounded-2xl p-6 shadow-sm border border-border hover:shadow-lg transition-all">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 rounded-xl bg-primary/10">
                  <Shield className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-semibold text-lg text-card-foreground">Business Prerequisites</h3>
              </div>
              <p className="text-muted-foreground text-sm">
                Web presence, sales accreditations, and case study requirements (BUSP-001 to BUSP-003)
              </p>
            </div>
            
            <div className="bg-card rounded-2xl p-6 shadow-sm border border-border hover:shadow-lg transition-all">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 rounded-xl bg-primary/10">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-semibold text-lg text-card-foreground">People Prerequisites</h3>
              </div>
              <p className="text-muted-foreground text-sm">
                Personnel skills, training strategies, and certification tracking (PEOP-001)
              </p>
            </div>
            
            <div className="bg-card rounded-2xl p-6 shadow-sm border border-border hover:shadow-lg transition-all">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 rounded-xl bg-primary/10">
                  <RefreshCw className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-semibold text-lg text-card-foreground">Governance Prerequisites</h3>
              </div>
              <p className="text-muted-foreground text-sm">
                Supplier management, operations improvement, and sustainability commitment (GOVP-001 to GOVP-003)
              </p>
            </div>
            
            <div className="bg-card rounded-2xl p-6 shadow-sm border border-border hover:shadow-lg transition-all">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 rounded-xl bg-primary/10">
                  <Cloud className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-semibold text-lg text-card-foreground">Platform Prerequisites</h3>
              </div>
              <p className="text-muted-foreground text-sm">
                Expert design reviews by AWS-certified Solutions Architects (PLATP-001)
              </p>
            </div>
            
            <div className="bg-card rounded-2xl p-6 shadow-sm border border-border hover:shadow-lg transition-all">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 rounded-xl bg-primary/10">
                  <Shield className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-semibold text-lg text-card-foreground">Security Prerequisites</h3>
              </div>
              <p className="text-muted-foreground text-sm">
                Access key exposure detection, public resource monitoring, and security automation (SECP-001+)
              </p>
            </div>
            
            <div className="bg-card rounded-2xl p-6 shadow-sm border border-border hover:shadow-lg transition-all">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 rounded-xl bg-primary/10">
                  <Clock className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-semibold text-lg text-card-foreground">Technical Validations</h3>
              </div>
              <p className="text-muted-foreground text-sm">
                Third-party auditor assessments during one-day technical validation sessions
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-24 bg-background">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl text-foreground">
              Why MSPs Choose Standardly
            </h2>
            <p className="mt-4 text-muted-foreground max-w-3xl mx-auto text-lg">
              Stop scrambling before audits. Stay continuously compliant instead.
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-card rounded-2xl p-8 shadow-sm border border-border hover:shadow-lg transition-all">
              <div className="flex flex-col space-y-4">
                <div className="p-3 rounded-xl bg-accent/10 w-fit">
                  <CheckCircle className="h-6 w-6 text-accent" />
                </div>
                <h3 className="font-semibold text-xl text-card-foreground">Always Audit-Ready</h3>
                <p className="text-muted-foreground">
                  Maintain continuous compliance instead of last-minute scrambles.
                </p>
              </div>
            </div>
            
            <div className="bg-card rounded-2xl p-8 shadow-sm border border-border hover:shadow-lg transition-all">
              <div className="flex flex-col space-y-4">
                <div className="p-3 rounded-xl bg-accent/10 w-fit">
                  <Clock className="h-6 w-6 text-accent" />
                </div>
                <h3 className="font-semibold text-xl text-card-foreground">Save Prep Time</h3>
                <p className="text-muted-foreground">
                  Reduce audit preparation from weeks to days with organized evidence.
                </p>
              </div>
            </div>
            
            <div className="bg-card rounded-2xl p-8 shadow-sm border border-border hover:shadow-lg transition-all">
              <div className="flex flex-col space-y-4">
                <div className="p-3 rounded-xl bg-accent/10 w-fit">
                  <FileCheck className="h-6 w-6 text-accent" />
                </div>
                <h3 className="font-semibold text-xl text-card-foreground">Clear Guidance</h3>
                <p className="text-muted-foreground">
                  Know exactly what evidence is required and in what format.
                </p>
              </div>
            </div>
            
            <div className="bg-card rounded-2xl p-8 shadow-sm border border-border hover:shadow-lg transition-all">
              <div className="flex flex-col space-y-4">
                <div className="p-3 rounded-xl bg-accent/10 w-fit">
                  <RefreshCw className="h-6 w-6 text-accent" />
                </div>
                <h3 className="font-semibold text-xl text-card-foreground">Smooth Renewals</h3>
                <p className="text-muted-foreground">
                  Breeze through Performance-Based Renewals with historical tracking.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-24 bg-brand-gradient">
        <div className="container px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto space-y-8">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl text-white">
              Ready to Simplify Your MSP Audits?
            </h2>
            <p className="text-white/80 text-lg">
              Join AWS Managed Service Providers who trust Standardly to streamline their compliance journey.
            </p>
            <Button 
              size="lg" 
              onClick={openWaitlistForm} 
              className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-8 py-6 text-lg font-medium"
            >
              Join Waitlist <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-background">
        <div className="container py-12 px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <img 
                  src={standardlyLogo} 
                  alt="Standardly Logo" 
                  className="h-7 w-auto" 
                />
              </div>
              <p className="text-sm text-muted-foreground">
                The intelligent platform helping AWS MSP Partners manage audits, track controls, and maintain continuous compliance.
              </p>
            </div>
          </div>
          
          <div className="border-t border-border mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground">
              © 2025 Standardly. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-muted-foreground hover:text-foreground transition-colors text-sm">Privacy</Link>
              <a href="#" className="text-muted-foreground hover:text-foreground transition-colors text-sm">Terms</a>
              <a href="#" className="text-muted-foreground hover:text-foreground transition-colors text-sm">Cookies</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Waitlist Dialog */}
      <HubSpotWaitlistModal open={showWaitlistForm} onOpenChange={setShowWaitlistForm} />
    </div>
  );
};

export default Index;
